import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, ViewEncapsulation} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonFieldService} from '../../../../services/common/common-field.service';
import {InternalTradeOrderViewService} from '../../../../services/internal/internal-trade-order-view.service';
import {TableColumnConfig} from "../../../../model/api/table-column-config";
import {filter, finalize, Observable, Subscription, takeUntil, timer} from "rxjs";
import {ApiValidation} from "../../../../model/api/api-validation";
import {ClearingSelectionInfo} from "../../../../model/clearing/clearing-selection-info";
import {ClearingAllocationTaskService} from "../../../../services/clearing/clearing-allocation-task.service";
import {ClearingAllocationService} from "../../../../services/clearing/clearing-allocation.service";
import {TableFilter} from "../../../../model/api/table-filter";
import {TradeDetailComponent} from "./trade-detail/trade-detail.component";
import {
  TradeSelectionTableDialogComponent
} from "./selection/selection-table-dialog/trade-selection-table-dialog.component";
import {ThemeService} from "../../../../services/tools/theme.service";
import {ClearingTradeInfoService} from "../../../../services/clearing/clearing-trade-info.service";
import {ClearingInfoLoadRequest} from "../../../../model/clearing/clearing-info-load-request";
import {TradeTableBaseComponent} from "./trade-table-base.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {WebTradeOrderView} from "../../../../model/internal/web-trade-order-view";
import {CCRoles} from "../../../../model/security/cc-roles-type";
import {ClearingFlexService} from "../../../../services/clearing/flex/clearing-flex.service";
import {FlexEditFormDialogComponent} from "../../flex/flex-edit-form-dialog/flex-edit-form-dialog.component";
import {
  FlexBatchEditFormDialogComponent
} from "../../flex/flex-batch-edit-form-dialog/flex-batch-edit-form-dialog.component";
import {ClearingTableConfigService} from "../../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../../services/clearing/clearing-selection.service";
import {ClearingTradeWsService} from "../../../../services/clearing/ws/clearing-trade-ws.service";
import {ClearingTableSyncWsService} from "../../../../services/clearing/ws/clearing-table-sync-ws.service";
import {SelectionType} from "../../../../crystal-elements/cc-vscroll-table/cc-vscroll-table.component";

export type CtrlAMode = 'ALL' | 'ALL_OPTIM' | 'ALL_IDS';

@Component({
  selector: 'app-clearing-trade-table[userView]',
  templateUrl: './trade-table.component.html',
  styleUrls: ['../clearing-table-base.component.scss', 'trade-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeTableComponent extends TradeTableBaseComponent {

  isSelectionInfoEnabled: boolean = true;
  isSelectionInfoEnabledLock: boolean = false;
  isSelectionInfoClosed: boolean = false;

  ctrlAMode: CtrlAMode = 'ALL_OPTIM';
  selectionType: SelectionType = 'CUSTOM';

  // form
  @ViewChild('tradeDetailDialog')
  tradeDetailDialog: TradeDetailComponent;
  @ViewChild('tradeSelectionDialog')
  tradeSelectionDialog: TradeSelectionTableDialogComponent;

  private minItemCountForSelectionDetail: number = 2;
  private selectionSummaryRequestVersion: number = 0;
  private tradeSelectionInfoRefreshSubscription?: Subscription;

  @ViewChild('flexDialogEditForm')
  flexDialogEditForm: FlexEditFormDialogComponent;

  @ViewChild('flexDialogBatchEditForm')
  flexDialogBatchEditForm: FlexBatchEditFormDialogComponent;

  constructor(cdref: ChangeDetectorRef,
              allocationService: ClearingAllocationService,
              private clearingFlexService: ClearingFlexService,
              allocationTaskService: ClearingAllocationTaskService,
              protected commonFieldService: CommonFieldService,
              protected userViewService: InternalTradeOrderViewService,
              public appConfigService: AppConfigService,
              clearingWsService: ClearingTradeWsService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              public translateService: TranslateService,
              public themeService: ThemeService,
              protected clearingTradeInfoService: ClearingTradeInfoService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService) {
    super(cdref,
      allocationService,
      allocationTaskService,
      commonFieldService,
      userViewService,
      appConfigService,
      clearingWsService,
      messageService,
      confirmationService,
      translateService,
      themeService,
      tableConfigService,
      clearingCookieService,
      clearingTableSyncWsService,
      clearingSelectionService,
    clearingTradeInfoService);
    this.origin = WebTradeOrderView.ORIGIN.TRADE;
    this.transPartKey = 'clearing.trade';

    this.isSelectionInfoEnabled = clearingCookieService.isSelectionInfo();
    this.isSelectionInfoEnabledLock = clearingCookieService.isSelectionInfoLock();
    this.ctrlAMode = clearingCookieService.loadTradeCtrlAMode();
  }

  protected initCommandButtons() {
    super.initCommandButtons();
    if (this.appConfigService.flexEnabled && (this.isFlexClearer())) {
      const flexItems = [
        {
          id: 'editFlexTrade',
          label: this.getTranslation('page.' + this.transPartKey + '.table.edit_flex_trade'),
          icon: 'fas fa-pen cc-fa-flex-edit',
          command: () => this.editFlexTrades(),
        },
        {
          id: 'batchEditFlexTrade',
          label: this.getTranslation('page.' + this.transPartKey + '.table.bacth_edit_flex_trade'),
          icon: 'fas fa-pen-to-square cc-fa-flex-edit',
          command: () => this.batchEditFlexTrades(),
        },
        {
          id: 'deleteFlexTrade',
          label: this.getTranslation('page.' + this.transPartKey + '.table.delete_flex_trade'),
          icon: 'fas fa-trash-can cc-fa-flex-delete',
          command: () => this.deleteFlexTrades()
        },
      ];
      this.tableContextMenuConfig.push(
        {
          id: 'flexmanagement',
          label: this.getTranslation('page.' + this.transPartKey + '.table.flex_management'),
          icon: 'fas cc-fa-flex fa-grip-lines',
          items: flexItems
        },
      );
      this.headerButtonConfig.push(
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.flex_management'),
          icon: 'fas cc-fa-flex fa-grip-lines',
          class: 'p-button-help cc-table-menu',
          id: 'flexmanagement',
          enabled: () => this.flexManagementEnabled(),
          items: flexItems,
        },);
    }
    this.tableContextMenuConfig.push(
      {
        separator: true
      },
      {
        id: 'showDetail',
        label: this.getTranslation('page.' + this.transPartKey + '.table.zoom_trade'),
        icon: 'fas fa-magnifying-glass',
        command: () => this.showDetail()
      },
    )
    this.headerButtonConfig.push(
      {
        tooltip: this.getTranslation('page.' + this.transPartKey + '.table.zoom_trade'),
        icon: 'fas fa-magnifying-glass-plus',
        class: ' p-button-info',
        command: () => this.showDetail(),
        enabled: () => this.showDetailEnabled()
      });
  }

  protected isFlexClearer(): boolean {
    return this.hasRoles(CCRoles.ROLES_CLEARING_FLEX_REQUEST);
  }

  protected refreshTable() {
    super.refreshTable();
    if (!this.tableConfig) {
      return;
    }
    let configToAdjust: TableColumnConfig = this.tableConfig.find(value => value.field === 'volume');
    if (configToAdjust) {
      configToAdjust.mapId = (record: any) => {
        if (record.clearings && record.clearings.length > 0) {
          return record.volume - record.clearings.map(value => value.volume).reduce((previousValue, currenValue) => previousValue + currenValue, 0);
        }
        return record.volume;
      }
    }
    configToAdjust = {field: 'clearingVolume', hidden: true};
    configToAdjust.mapId = record => record.clearings ? record.clearings.map(value => value.volume).reduce((previousValue, currenValue) => previousValue + currenValue, 0) : 0;
    this.tableConfig.push(configToAdjust);
    this.markForCheck();
  }

  showDetail(): void {
    this.setLoading();
    this.allocationService.getSignificantTrades(this.selectedIds).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.setNotLoading())
    ).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.tradeDetailDialog.open(apiValidation.item[0]);
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }


  editFlexTrades(): void {
    this.setLoading();
    this.allocationService.getFullTrades(this.selectedIds).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.setNotLoading())
    ).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.flexDialogEditForm.openDialog(apiValidation.item)
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }

  batchEditFlexTrades(): void {
    this.setLoading();
    this.allocationService.getSignificantTrades(this.selectedIds).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.setNotLoading())
    ).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.flexDialogBatchEditForm.openDialog(apiValidation.item, this.selectedIds)
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }

  deleteFlexTrades(): void {
    const currentSelectedIds = [...this.selectedIds];
    const header = this.getTranslation('common.table.delete_item')
      + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + (currentSelectedIds.length === 1 ? '' : 's');
    const message = currentSelectedIds.length > 1 ? (this.getTranslation('common.dialog.confirm_delete_plu')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's?')
      : (this.getTranslation('common.dialog.confirm_delete_sing') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name') + '?');
    this.confirmCommand(() => {
      this.setLoading();
      // this.tableLoadingCount++;
      this.clearingFlexService.deleteTrades(this.selectedIds).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
        if (apiValidation.success) {
          this.infoMessage(apiValidation.message)
        } else {
          this.errorMessage(apiValidation.message);
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    }, header, message, 'common.dialog.delete');
  }

  refreshContextMenu() {
    this.tableContextMenuConfig.forEach(menu => {
      if (menu.id === 'showDetail') {
        menu.disabled = !this.showDetailEnabled();
      } else if (menu.id === 'split-allocation') {
        menu.disabled = !this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg);
      } else if (menu.id === 'single-allocation') {
        menu.disabled = !this.singleAllocationEnabled();
      } else if (menu.id === 'single-allocation-add') {
        menu.disabled = false;
      } else if (menu.id === 'overrideCustomReference') {
        menu.disabled = !this.overrideCustomReferenceEnabled();
      } else if (menu.id === 'flexmanagement') {
        menu.disabled = !this.flexManagementEnabled();
        if (!menu.disabled)
          menu.items.forEach(value => {
            if (value.id === 'editFlexTrade')
              value.disabled = !this.flexManagementEnabled() && this.selectedIds.length < this.appConfigService.flexMaxEditCount;
            else if (value.id === 'batchEditFlexTrade')
              value.disabled = !this.flexManagementEnabled();
            else if (value.id === 'deleteFlexTrade')
              value.disabled = !this.flexManagementEnabled();
          });
      }
    });
    this.tableContextMenuConfig = [...this.tableContextMenuConfig];
    // this.detectChanges();
  }

  rowDblClick(event) {
    this.ctrlShift(event, false);
  }

  ctrlShift(event, forceSingle: boolean = true) {
    timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isReadOnly()
          && (this.singleAllocationEnabled() || this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg))) {
          this.allocateFromSelection(undefined, forceSingle);
        } else if (this.showDetailEnabled()) {
          this.showDetail();
        }
      });
  }

  private showDetailEnabled() {
    return this.selectedIds.length == 1;
  }

  private flexManagementEnabled() {
    if (this.selectedIds.length == 0) return false;
    let hasFlexTrade = true;
    this.currentClearingSelectionInfosByLeg.forEach(value => {
      hasFlexTrade = hasFlexTrade && value.exchangeCode.endsWith(this.appConfigService.flexGwSuffix);
    });

    return hasFlexTrade;
  }

  filterSelectedIds(event: any) {
    const request = this.tableConfigService.constructRequest(JSON.parse(JSON.stringify(this.tableCurrentRequest)));
    request.originEvent = undefined;
    request.syncMetadata = undefined;
    const newFilter = new TableFilter();
    newFilter.field = 'id';
    newFilter.value = event.join(',');
    newFilter.matchMode = 'in';
    newFilter.operator = 'and';
    request.filters.push(newFilter);
    this.ccTable.setFiltersAndSort(request);
    this.tableCurrentRequest = request;
    this.selectedIds.splice(0);
    this.selectedIds.push(...event)
    this.selectedIds = [...this.selectedIds];
    this.refreshSelectionInfos();
    this.forceReload = true;
  }

  protected reloadClearingSectionInfos() {
    super.reloadClearingSectionInfos();
    this.refreshTradeSelectionInfo();
  }

  public toggleSelectionInfoEnabled() {
    if (this.isSelectionInfoClosed) {
      this.isSelectionInfoClosed = false;
      this.refreshTradeSelectionInfo();
    } else if (this.isSelectionInfoEnabled && !this.isSelectionInfoEnabledLock) {
      this.isSelectionInfoEnabledLock = true;
      // this.refreshTradeSelectionInfo();
      this.clearingCookieService.storeSelectionInfoLock(this.isSelectionInfoEnabledLock);
    } else {
      this.isSelectionInfoEnabled = !this.isSelectionInfoEnabled;
      this.isSelectionInfoEnabledLock = false;
      this.refreshTradeSelectionInfo();
      this.clearingCookieService.storeSelectionInfo(this.isSelectionInfoEnabled);
      this.clearingCookieService.storeSelectionInfoLock(this.isSelectionInfoEnabledLock);
    }
    this.infoMessage(this.getTranslation('page.' + this.transPartKey + '.table.' +
      (this.isSelectionInfoEnabledLock ? 'lock_selection_info_mssg' : (this.isSelectionInfoEnabled ? 'enable_selection_info_mssg' : 'disable_selection_info_mssg'))
    ));
  }

  public toggleCtrlAllModeEnabled() {
    if (this.ctrlAMode === 'ALL') {
      this.ctrlAMode = 'ALL_IDS';
    } else if (this.ctrlAMode === 'ALL_OPTIM') {
      this.ctrlAMode = 'ALL';
    } else {
      this.ctrlAMode = 'ALL_OPTIM';
    }
    this.infoMessage(this.getTranslation('page.' + this.transPartKey + '.table.' + (this.ctrlAMode === 'ALL_IDS' ? 'ctrl_a_all_ids_mssg' :
        (this.ctrlAMode == 'ALL_OPTIM' ? 'ctrl_a_all_optim_mssg' : (this.ctrlAMode === 'ALL' ? 'ctrl_a_all_fix_mssg' : ''))
    )));
    this.clearingCookieService.storeTradeCtrlAMode(this.ctrlAMode);
    this.ccTable.clearSelection();
  }

  public refreshTradeSelectionInfo() {
    this.tradeSelectionInfoRefreshSubscription?.unsubscribe();

    const selectedIdsSnapshot = [...this.selectedIds];
    const canDisplaySelectionInfo = (this.isSelectionInfoEnabledLock || this.isSelectionInfoEnabled) && !this.isSelectionInfoClosed;
    const shouldShowDialog = canDisplaySelectionInfo && (
      (this.isSelectionInfoEnabledLock && selectedIdsSnapshot.length >= 1)
      || selectedIdsSnapshot.length >= this.minItemCountForSelectionDetail
    );

    this.tradeSelectionInfoRefreshSubscription = timer(250)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isSameSelection(selectedIdsSnapshot, this.selectedIds)) {
          return;
        }

        if (shouldShowDialog) {
          const isHidden = !this.tradeSelectionDialog.selectionTradeDialogVisible;
          this.tradeSelectionDialog.show();
          if (isHidden) {
            this.tradeSelectionDialog.forceReload();
          } else {
            this.tradeSelectionDialog.reloadTable();
          }
          return;
        }

        if (this.tradeSelectionDialog.selectionTradeDialogVisible) {
          if (canDisplaySelectionInfo) {
            this.tradeSelectionDialog.reloadTable();
          }

          if ((selectedIdsSnapshot.length < this.minItemCountForSelectionDetail && !this.isSelectionInfoEnabledLock)
            || !canDisplaySelectionInfo) {
            if (!this.ccTable.mouseDown) {
              this.tradeSelectionDialog.hide();
            }
          }
        }
      });
  }

  private isSameSelection(left: string[], right: string[]): boolean {
    if (left === right) {
      return true;
    }

    if (!left || !right || left.length !== right.length) {
      return false;
    }

    for (let i = 0; i < left.length; i++) {
      if (left[i] !== right[i]) {
        return false;
      }
    }

    return true;
  }

  public closeTradeSelectionInfo() {
    if (this.tradeSelectionDialog.selectionTradeDialogVisible) {
      this.tradeSelectionDialog.close();
    }
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest) {
    const requestVersion = ++this.selectionSummaryRequestVersion;
    return this.clearingTradeInfoService.getSelectionSummary(summaryRequest).pipe(
      filter(() => requestVersion === this.selectionSummaryRequestVersion)
    );
  }

  protected getSelectionInfoLegs(request: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionInfo[]>> {
    return this.clearingTradeInfoService.getSelectionInfoLegs(request);
  }

  public onTableSelectedIdsChange(selectedIds: string[]) {
    this.isSelectionInfoClosed = false;
    this.refreshSelectionInfos();
    this.selectedIdsChange.emit(selectedIds);
  }

}
